import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config";

const UploadSection = () => {
  // Estados principais
  const [vagas, setVagas] = useState([]);
  const [vagaSelecionada, setVagaSelecionada] = useState(null);
  const [candidatoSelecionado, setCandidatoSelecionado] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0, texto: "" });
  const [modo, setModo] = useState("lista"); // lista, nova-vaga, adicionar-curriculos
  const [activeTab, setActiveTab] = useState("resumo");
  
  // Estados de upload
  const [requisitoArquivo, setRequisitoArquivo] = useState(null);
  const [requisitoTexto, setRequisitoTexto] = useState("");
  const [modoRequisito, setModoRequisito] = useState("arquivo"); // arquivo ou texto
  const [curriculosArquivos, setCurriculosArquivos] = useState([]);
  const [dragOver, setDragOver] = useState({ requisitos: false, curriculos: false });

  useEffect(() => {
    carregarVagas();
  }, []);

  const carregarVagas = async () => {
    try {
      const response = await axios.get(`${API_URL}/vagas`);
      setVagas(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar vagas:", error);
    }
  };

  const carregarVaga = async (vagaId) => {
    try {
      const response = await axios.get(`${API_URL}/vagas/${vagaId}`);
      const vagaAtualizada = { ...response.data, candidatos: response.data.candidatos || [] };
      setVagaSelecionada(vagaAtualizada);
      setCandidatoSelecionado(null);
      setRefreshKey(prev => prev + 1);
      return vagaAtualizada;
    } catch (error) {
      console.error("Erro ao carregar vaga:", error);
      return null;
    }
  };

  const criarVaga = async () => {
    if (!requisitoArquivo && !requisitoTexto.trim()) {
      alert("Adicione o arquivo de requisitos ou escreva os requisitos da vaga.");
      return;
    }

    setLoading(true);
    setProgresso({ atual: 0, total: 1, texto: "Criando vaga e extraindo informações..." });

    const formData = new FormData();
    if (modoRequisito === "arquivo" && requisitoArquivo) {
      formData.append("requisito", requisitoArquivo);
    } else if (modoRequisito === "texto" && requisitoTexto.trim()) {
      formData.append("requisito_texto", requisitoTexto.trim());
    }

    try {
      const response = await axios.post(
        `${API_URL}/analisar/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        }
      );

      await carregarVagas();
      setRequisitoArquivo(null);
      setRequisitoTexto("");
      setModoRequisito("arquivo");
      setModo("lista");
      
      // Selecionar a vaga criada
      if (response.data.vaga_id) {
        carregarVaga(response.data.vaga_id);
      }
    } catch (error) {
      console.error("Erro ao criar vaga:", error);
      alert("Erro ao criar vaga.");
    } finally {
      setLoading(false);
    }
  };

  const adicionarCandidatos = async () => {
    if (!vagaSelecionada || curriculosArquivos.length === 0) {
      alert("Selecione os currículos.");
      return;
    }

    setLoading(true);
    const total = curriculosArquivos.length;

    const formData = new FormData();
    curriculosArquivos.forEach((file) => formData.append("curriculos", file));

    setProgresso({ atual: 0, total, texto: `Enviando ${total} currículo(s)...` });

    try {
      // Enviar currículos e receber job_id
      const response = await axios.post(
        `${API_URL}/vagas/${vagaSelecionada.id}/candidatos`, 
        formData, 
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        }
      );

      const data = response.data;

      // Verificar se há currículos duplicados
      if (data.duplicados > 0) {
        alert(`ℹ️ ${data.duplicados} currículo(s) já foram analisados e foram ignorados.`);
      }

      // Verificar se retornou job_id (processamento assíncrono)
      if (data.job_id) {
        console.log(`✅ Job criado: ${data.job_id}`);
        setProgresso({ atual: 0, total, texto: "Processamento iniciado..." });

        // Monitorar progresso do job
        const monitorarJob = async () => {
          try {
            const statusResponse = await axios.get(`${API_URL}/jobs/${data.job_id}`);
            const status = statusResponse.data;

            console.log(`📊 Status do job: ${status.status} - ${status.progresso}%`);

            // Atualizar progresso na UI
            if (status.processados !== undefined && status.total !== undefined) {
              setProgresso({
                atual: status.processados,
                total: status.total,
                texto: `Processando ${status.processados}/${status.total} currículo(s)... ${status.progresso}%`
              });
            }

            // Verificar se concluiu
            if (status.status === "concluido") {
              console.log("✅ Processamento concluído!");
              setProgresso({ atual: total, total, texto: "Concluído! Atualizando..." });
              
              // Recarregar vaga com os novos candidatos
              await carregarVaga(vagaSelecionada.id);
              await carregarVagas();
              
              setCurriculosArquivos([]);
              setModo("lista");
              setLoading(false);
              return true; // Parar monitoramento
            } else if (status.status === "erro") {
              console.error("❌ Erro no processamento:", status.erro);
              alert(`Erro ao processar currículos: ${status.erro || "Erro desconhecido"}`);
              setLoading(false);
              return true; // Parar monitoramento
            }

            return false; // Continuar monitoramento
          } catch (error) {
            console.error("Erro ao consultar status do job:", error);
            return false; // Continuar tentando
          }
        };

        // Polling a cada 3 segundos
        const intervalId = setInterval(async () => {
          const devePararMonitoramento = await monitorarJob();
          if (devePararMonitoramento) {
            clearInterval(intervalId);
          }
        }, 3000);

        // Primeira verificação imediata
        const devePararMonitoramento = await monitorarJob();
        if (devePararMonitoramento) {
          clearInterval(intervalId);
        }

      } else {
        // Fallback: resposta antiga (síncrona)
        console.log("⚠️ Resposta síncrona (backend antigo)");
        await carregarVaga(vagaSelecionada.id);
        await carregarVagas();
        setCurriculosArquivos([]);
        setModo("lista");
        setLoading(false);
      }

    } catch (error) {
      console.error("Erro ao adicionar candidatos:", error);
      alert(`Erro ao analisar currículos: ${error.response?.data?.detail || error.message}`);
      setLoading(false);
    }
  };

  const deletarVaga = async (vagaId) => {
    if (!window.confirm("Tem certeza que deseja excluir esta vaga e todos os candidatos?")) return;
    
    try {
      await axios.delete(`${API_URL}/vagas/${vagaId}`);
      await carregarVagas();
      if (vagaSelecionada?.id === vagaId) {
        setVagaSelecionada(null);
        setCandidatoSelecionado(null);
      }
    } catch (error) {
      console.error("Erro ao deletar vaga:", error);
    }
  };

  const removerCandidato = async (candidatoId) => {
    if (!window.confirm("Remover este candidato?")) return;
    
    try {
      await axios.delete(`${API_URL}/vagas/${vagaSelecionada.id}/candidatos/${candidatoId}`);
      await carregarVaga(vagaSelecionada.id);
      await carregarVagas();
      if (candidatoSelecionado?.id === candidatoId) {
        setCandidatoSelecionado(null);
      }
    } catch (error) {
      console.error("Erro ao remover candidato:", error);
    }
  };

  const handleDrop = (e, tipo) => {
    e.preventDefault();
    setDragOver({ ...dragOver, [tipo]: false });
    
    const files = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".docx")
    );
    
    if (files.length === 0) {
      alert("Apenas PDF ou DOCX");
      return;
    }
    
    if (tipo === "requisitos") {
      setRequisitoArquivo(files[0]); // Apenas um arquivo
    } else {
      setCurriculosArquivos([...curriculosArquivos, ...files]);
    }
  };

  const handleFileChange = (e, tipo) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".docx")
    );
    
    if (tipo === "requisitos" && files.length > 0) {
      setRequisitoArquivo(files[0]); // Apenas um arquivo
    } else if (tipo === "curriculos") {
      setCurriculosArquivos([...curriculosArquivos, ...files]);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "from-emerald-500 to-green-500";
    if (score >= 60) return "from-yellow-500 to-amber-500";
    if (score >= 40) return "from-orange-500 to-red-400";
    return "from-red-500 to-red-600";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-emerald-50 border-emerald-200";
    if (score >= 60) return "bg-yellow-50 border-yellow-200";
    if (score >= 40) return "bg-orange-50 border-orange-200";
    return "bg-red-50 border-red-200";
  };

  const getRecomendacaoStyle = (rec) => {
    const styles = {
      altamente_recomendado: "bg-emerald-100 text-emerald-800 border-emerald-300",
      recomendado: "bg-blue-100 text-blue-800 border-blue-300",
      recomendado_com_ressalvas: "bg-yellow-100 text-yellow-800 border-yellow-300",
      nao_recomendado: "bg-red-100 text-red-800 border-red-300",
    };
    return styles[rec] || "bg-gray-100 text-gray-800";
  };

  const getRecomendacaoLabel = (rec) => {
    const labels = {
      altamente_recomendado: "✨ Altamente Recomendado",
      recomendado: "👍 Recomendado",
      recomendado_com_ressalvas: "⚠️ Com Ressalvas",
      nao_recomendado: "❌ Não Recomendado",
    };
    return labels[rec] || rec;
  };

  const getStatusStyle = (status) => {
    const styles = {
      atende: "bg-green-100 text-green-700",
      parcial: "bg-yellow-100 text-yellow-700",
      nao_atende: "bg-red-100 text-red-700",
    };
    return styles[status] || "bg-gray-100";
  };

  // Função para processar arquivos de uma pasta (webkitdirectory)
  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".docx")
    );
    if (files.length > 0) {
      setCurriculosArquivos([...curriculosArquivos, ...files]);
    }
  };

  // Componente de Upload Unificado para Currículos
  const UploadCurriculosArea = () => (
    <div className="mb-4">
      <label className="block text-white font-medium mb-2">📄 Currículos para Análise</label>
      <div
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-all ${
          dragOver.curriculos ? "border-blue-500 bg-blue-500/10" :
          curriculosArquivos.length > 0 ? "border-emerald-500 bg-emerald-500/10" :
          "border-slate-600 hover:border-slate-500"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver({ ...dragOver, curriculos: true }); }}
        onDragLeave={() => setDragOver({ ...dragOver, curriculos: false })}
        onDrop={(e) => handleDrop(e, "curriculos")}
      >
        {curriculosArquivos.length > 0 ? (
          <div>
            <div className="text-3xl mb-2">📄</div>
            <p className="text-white font-medium">{curriculosArquivos.length} arquivo(s) selecionado(s)</p>
            <div className="text-slate-400 text-xs mt-2 max-h-32 overflow-y-auto px-4">
              {curriculosArquivos.map((f, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-slate-700/30">
                  <span className="truncate flex-1 text-left">{f.name}</span>
                  <button
                    onClick={() => setCurriculosArquivos(curriculosArquivos.filter((_, idx) => idx !== i))}
                    className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0"
                  >×</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-center mt-4 flex-wrap">
              {/* Adicionar mais arquivos */}
              <input
                type="file"
                accept=".pdf,.docx"
                multiple
                onChange={(e) => handleFileChange(e, "curriculos")}
                className="hidden"
                id="add-more-files"
              />
              <label
                htmlFor="add-more-files"
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs cursor-pointer hover:bg-blue-700 flex items-center gap-1"
              >
                📄 + Arquivos
              </label>
              
              {/* Adicionar pasta */}
              <input
                type="file"
                accept=".pdf,.docx"
                multiple
                webkitdirectory=""
                directory=""
                onChange={handleFolderSelect}
                className="hidden"
                id="add-more-folder"
              />
              <label
                htmlFor="add-more-folder"
                className="px-3 py-2 bg-purple-600 text-white rounded-lg text-xs cursor-pointer hover:bg-purple-700 flex items-center gap-1"
              >
                📁 + Pasta
              </label>
              
              <button
                onClick={() => setCurriculosArquivos([])}
                className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/30"
              >
                🗑️ Limpar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-5xl mb-3 opacity-60">📁</div>
            <p className="text-slate-300 text-sm mb-1">Arraste arquivos aqui</p>
            <p className="text-slate-500 text-xs mb-4">ou escolha uma das opções abaixo</p>
            
            <div className="flex gap-3 justify-center flex-wrap">
              {/* Selecionar Arquivos */}
              <input
                type="file"
                accept=".pdf,.docx"
                multiple
                onChange={(e) => handleFileChange(e, "curriculos")}
                className="hidden"
                id="upload-files"
              />
              <label
                htmlFor="upload-files"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 text-sm flex items-center gap-2"
              >
                📄 Selecionar Arquivos
              </label>
              
              {/* Selecionar Pasta */}
              <input
                type="file"
                accept=".pdf,.docx"
                multiple
                webkitdirectory=""
                directory=""
                onChange={handleFolderSelect}
                className="hidden"
                id="upload-folder"
              />
              <label
                htmlFor="upload-folder"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg cursor-pointer hover:bg-purple-700 text-sm flex items-center gap-2"
              >
                📁 Selecionar Pasta
              </label>
            </div>
            
            <p className="text-slate-500 text-xs mt-4">Aceita PDF e DOCX</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar - Lista de Vagas */}
      <div className="w-80 bg-slate-800/50 backdrop-blur-xl border-r border-slate-700/50 flex flex-col">
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <span className="text-xl">💼</span> Vagas
            </h2>
            <button
              onClick={() => { setModo("nova-vaga"); setVagaSelecionada(null); setCandidatoSelecionado(null); }}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
            >
              + Nova
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {vagas.length === 0 ? (
            <p className="text-center text-slate-500 text-sm py-8">Nenhuma vaga cadastrada</p>
          ) : (
            vagas.map((vaga) => (
              <div
                key={vaga.id}
                onClick={() => { carregarVaga(vaga.id); setModo("lista"); setCandidatoSelecionado(null); }}
                className={`rounded-xl p-3 cursor-pointer transition ${
                  vagaSelecionada?.id === vaga.id
                    ? "bg-blue-600/30 border border-blue-500"
                    : "bg-slate-700/30 border border-slate-600/50 hover:bg-slate-700/50"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{vaga.titulo}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {vaga.total_candidatos} candidato(s)
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-slate-500">
                    {new Date(vaga.criado_em).toLocaleDateString("pt-BR")}
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); deletarVaga(vaga.id); }}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
              Análise Curricular com IA
            </h1>
            <p className="text-slate-400">Gerencie vagas e analise candidatos</p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-8 text-center">
              <svg className="animate-spin h-12 w-12 mx-auto mb-4 text-blue-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-white text-lg">{progresso.texto}</p>
            </div>
          )}

          {/* Nova Vaga */}
          {!loading && modo === "nova-vaga" && (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
              <h2 className="text-xl font-semibold text-white mb-6">📋 Criar Nova Vaga</h2>
              
              {/* Tabs para escolher modo */}
              <div className="flex mb-6 bg-slate-700/30 rounded-lg p-1">
                <button
                  onClick={() => setModoRequisito("arquivo")}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                    modoRequisito === "arquivo"
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  📄 Upload de Arquivo
                </button>
                <button
                  onClick={() => setModoRequisito("texto")}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                    modoRequisito === "texto"
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  ✏️ Escrever Requisitos
                </button>
              </div>

              {/* Modo Arquivo */}
              {modoRequisito === "arquivo" && (
                <div className="mb-4">
                  <label className="block text-white font-medium mb-2">📄 Arquivo de Requisitos da Vaga (PDF ou DOCX)</label>
                  <div
                    className={`rounded-xl border-2 border-dashed p-6 text-center transition-all ${
                      dragOver.requisitos ? "border-blue-500 bg-blue-500/10" :
                      requisitoArquivo ? "border-emerald-500 bg-emerald-500/10" :
                      "border-slate-600 hover:border-slate-500"
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver({ ...dragOver, requisitos: true }); }}
                    onDragLeave={() => setDragOver({ ...dragOver, requisitos: false })}
                    onDrop={(e) => handleDrop(e, "requisitos")}
                  >
                    {requisitoArquivo ? (
                      <div>
                        <div className="text-4xl mb-2">📄</div>
                        <p className="text-white font-medium">{requisitoArquivo.name}</p>
                        <p className="text-slate-400 text-sm mt-1">{(requisitoArquivo.size / 1024).toFixed(1)} KB</p>
                        <button
                          onClick={() => setRequisitoArquivo(null)}
                          className="mt-3 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
                        >
                          Remover
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="text-4xl mb-2 opacity-50">📁</div>
                        <p className="text-slate-400 text-sm mb-3">Arraste o arquivo ou clique para selecionar</p>
                        <input
                          type="file"
                          accept=".pdf,.docx"
                          onChange={(e) => handleFileChange(e, "requisitos")}
                          className="hidden"
                          id="upload-requisito"
                        />
                        <label
                          htmlFor="upload-requisito"
                          className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 text-sm"
                        >
                          Selecionar Arquivo
                        </label>
                      </div>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs mt-2">O título da vaga será extraído automaticamente do documento.</p>
                </div>
              )}

              {/* Modo Texto */}
              {modoRequisito === "texto" && (
                <div className="mb-4">
                  <label className="block text-white font-medium mb-2">✏️ Requisitos da Vaga</label>
                  <textarea
                    value={requisitoTexto}
                    onChange={(e) => setRequisitoTexto(e.target.value)}
                    placeholder="Descreva os requisitos da vaga aqui...

Exemplo:
Título: Desenvolvedor Full Stack

Requisitos Obrigatórios:
- 3+ anos de experiência com React
- Conhecimento em Node.js
- Experiência com bancos de dados SQL

Requisitos Desejáveis:
- Conhecimento em AWS
- Experiência com metodologias ágeis

Responsabilidades:
- Desenvolver e manter aplicações web
- Participar de code reviews"
                    className="w-full h-64 px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                  />
                  <p className="text-slate-500 text-xs mt-2">O título da vaga será extraído automaticamente do texto.</p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setModo("lista"); setRequisitoArquivo(null); setRequisitoTexto(""); }}
                  className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={criarVaga}
                  disabled={(modoRequisito === "arquivo" && !requisitoArquivo) || (modoRequisito === "texto" && !requisitoTexto.trim())}
                  className={`flex-1 py-3 rounded-lg font-semibold ${
                    (modoRequisito === "arquivo" && !requisitoArquivo) || (modoRequisito === "texto" && !requisitoTexto.trim())
                      ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                  }`}
                >
                  Criar Vaga
                </button>
              </div>
            </div>
          )}

          {/* Adicionar Currículos */}
          {!loading && modo === "adicionar-curriculos" && vagaSelecionada && (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
              <h2 className="text-xl font-semibold text-white mb-2">📄 Adicionar Currículos</h2>
              <p className="text-slate-400 mb-6">Vaga: {vagaSelecionada.titulo}</p>

              <UploadCurriculosArea />

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { setModo("lista"); setCurriculosArquivos([]); }}
                  className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={adicionarCandidatos}
                  disabled={curriculosArquivos.length === 0}
                  className={`flex-1 py-3 rounded-lg font-semibold ${
                    curriculosArquivos.length === 0
                      ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                  }`}
                >
                  🚀 Analisar {curriculosArquivos.length} Currículo(s)
                </button>
              </div>
            </div>
          )}

          {/* Lista de Candidatos da Vaga */}
          {!loading && modo === "lista" && vagaSelecionada && !candidatoSelecionado && (
            <div className="space-y-6">
              {/* Header da Vaga */}
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{vagaSelecionada.titulo}</h2>
                    <p className="text-slate-400 mt-1">
                      {vagaSelecionada.candidatos?.length || 0} candidato(s) analisado(s)
                    </p>
                  </div>
                  <button
                    onClick={() => setModo("adicionar-curriculos")}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <span>+</span> Adicionar Currículos
                  </button>
                </div>
              </div>

              {/* Ranking de Candidatos */}
              {vagaSelecionada.candidatos?.length > 0 ? (
                <div key={refreshKey} className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">🏆 Ranking de Candidatos</h3>
                  <div className="space-y-3">
                    {vagaSelecionada.candidatos.map((candidato, idx) => (
                      <div
                        key={candidato.id}
                        onClick={() => setCandidatoSelecionado(candidato)}
                        className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl cursor-pointer hover:bg-slate-700/50 transition"
                      >
                        <div className="flex items-center gap-4">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx === 0 ? "bg-yellow-500 text-black" :
                            idx === 1 ? "bg-slate-400 text-black" :
                            idx === 2 ? "bg-amber-700 text-white" :
                            "bg-slate-600 text-white"
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-white font-medium">
                              {candidato.candidato?.nome || candidato.arquivo}
                            </p>
                            <p className="text-slate-400 text-sm">
                              {candidato.candidato?.cargo_atual || candidato.arquivo}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-2 py-1 rounded text-xs ${getRecomendacaoStyle(candidato.recomendacao)}`}>
                            {getRecomendacaoLabel(candidato.recomendacao)}
                          </span>
                          <span className={`px-4 py-2 rounded-full text-lg font-bold bg-gradient-to-r ${getScoreColor(candidato.pontuacao_geral || 0)} text-white`}>
                            {candidato.pontuacao_geral || 0}%
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); removerCandidato(candidato.id); }}
                            className="text-red-400 hover:text-red-300 p-2"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-12 text-center">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-slate-400 mb-4">Nenhum candidato analisado ainda</p>
                  <button
                    onClick={() => setModo("adicionar-curriculos")}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Adicionar Currículos
                  </button>
                </div>
              )}

              {/* Resumo da Vaga */}
              {vagaSelecionada.resumo && (
                <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    📋 Resumo da Vaga
                  </h3>
                  
                  {/* Info Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {vagaSelecionada.resumo.nivel && (
                      <div className="bg-slate-700/30 rounded-xl p-3 flex items-center gap-3">
                        <div className="text-2xl">📊</div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-400">Nível</p>
                          <p className="text-white font-medium text-sm">{vagaSelecionada.resumo.nivel}</p>
                        </div>
                      </div>
                    )}
                    {vagaSelecionada.resumo.modalidade && (
                      <div className="bg-slate-700/30 rounded-xl p-3 flex items-center gap-3">
                        <div className="text-2xl">🏢</div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-400">Modalidade</p>
                          <p className="text-white font-medium text-sm">{vagaSelecionada.resumo.modalidade}</p>
                        </div>
                      </div>
                    )}
                    {vagaSelecionada.resumo.tipo_contrato && (
                      <div className="bg-slate-700/30 rounded-xl p-3 flex items-center gap-3">
                        <div className="text-2xl">📝</div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-400">Contrato</p>
                          <p className="text-white font-medium text-sm">{vagaSelecionada.resumo.tipo_contrato}</p>
                        </div>
                      </div>
                    )}
                    {vagaSelecionada.resumo.experiencia && (
                      <div className="bg-slate-700/30 rounded-xl p-3 flex items-center gap-3">
                        <div className="text-2xl">⏱️</div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-400">Experiência</p>
                          <p className="text-white font-medium text-sm">{vagaSelecionada.resumo.experiencia}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Descrição */}
                  {vagaSelecionada.resumo.resumo && (
                    <div className="mb-6 p-4 bg-slate-700/20 rounded-xl border-l-4 border-blue-500">
                      <p className="text-slate-300 text-sm italic">{vagaSelecionada.resumo.resumo}</p>
                    </div>
                  )}

                  {/* Grid de Requisitos */}
                  <div className="space-y-4">
                    {/* Requisitos Obrigatórios */}
                    {vagaSelecionada.resumo.requisitos_obrigatorios?.length > 0 && (
                      <div className="bg-slate-700/20 rounded-xl p-4 w-full">
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <span className="text-red-400">●</span> Requisitos Obrigatórios
                        </h4>
                        <div className="space-y-2">
                          {vagaSelecionada.resumo.requisitos_obrigatorios.map((req, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-red-400 text-xs mt-1">▸</span>
                              <span className="text-slate-300 text-sm">{req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Requisitos Desejáveis */}
                    {vagaSelecionada.resumo.requisitos_desejaveis?.length > 0 && (
                      <div className="bg-slate-700/20 rounded-xl p-4 w-full">
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <span className="text-yellow-400">●</span> Requisitos Desejáveis
                        </h4>
                        <div className="space-y-2">
                          {vagaSelecionada.resumo.requisitos_desejaveis.map((req, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-yellow-400 text-xs mt-1">▸</span>
                              <span className="text-slate-300 text-sm">{req}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Habilidades */}
                  <div className="mt-4 space-y-4">
                    {/* Habilidades Técnicas */}
                    {vagaSelecionada.resumo.habilidades_tecnicas?.length > 0 && (
                      <div className="bg-slate-700/20 rounded-xl p-4 w-full">
                        <h4 className="text-white font-medium mb-3 text-sm">💻 Habilidades Técnicas</h4>
                        <div className="flex flex-wrap gap-2">
                          {vagaSelecionada.resumo.habilidades_tecnicas.map((skill, idx) => (
                            <span key={idx} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Soft Skills */}
                    {vagaSelecionada.resumo.habilidades_comportamentais?.length > 0 && (
                      <div className="bg-slate-700/20 rounded-xl p-4 w-full">
                        <h4 className="text-white font-medium mb-3 text-sm">🤝 Soft Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {vagaSelecionada.resumo.habilidades_comportamentais.map((skill, idx) => (
                            <span key={idx} className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Formação e Benefícios */}
                  <div className="mt-4 grid md:grid-cols-2 gap-4">
                    {vagaSelecionada.resumo.formacao && vagaSelecionada.resumo.formacao !== "Não especificado" && (
                      <div className="bg-slate-700/20 rounded-xl p-3">
                        <h4 className="text-white font-medium mb-1 text-sm">🎓 Formação</h4>
                        <p className="text-slate-300 text-sm">{vagaSelecionada.resumo.formacao}</p>
                      </div>
                    )}
                    
                    {vagaSelecionada.resumo.faixa_salarial && vagaSelecionada.resumo.faixa_salarial !== "Não informado" && (
                      <div className="bg-slate-700/20 rounded-xl p-3">
                        <h4 className="text-white font-medium mb-1 text-sm">💰 Faixa Salarial</h4>
                        <p className="text-slate-300 text-sm">{vagaSelecionada.resumo.faixa_salarial}</p>
                      </div>
                    )}
                  </div>

                  {/* Benefícios */}
                  {vagaSelecionada.resumo.beneficios?.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-white font-medium mb-2 text-sm">🎁 Benefícios</h4>
                      <div className="flex flex-wrap gap-2">
                        {vagaSelecionada.resumo.beneficios.map((ben, idx) => (
                          <span key={idx} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
                            {ben}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Detalhes do Candidato */}
          {!loading && candidatoSelecionado && (
            <div className="space-y-6">
              <button
                onClick={() => setCandidatoSelecionado(null)}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition"
              >
                ← Voltar ao Ranking
              </button>

              {/* Score Card */}
              <div className={`rounded-2xl border p-6 ${getScoreBg(candidatoSelecionado.pontuacao_geral || 0)}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800">
                      {candidatoSelecionado.candidato?.nome || "Candidato"}
                    </h2>
                    <p className="text-slate-600">{candidatoSelecionado.candidato?.cargo_atual || ""}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {candidatoSelecionado.candidato?.anos_experiencia || 0} anos de experiência
                    </p>
                  </div>
                  <div className="text-center">
                    <div className={`text-5xl font-bold bg-gradient-to-r ${getScoreColor(candidatoSelecionado.pontuacao_geral || 0)} bg-clip-text text-transparent`}>
                      {candidatoSelecionado.pontuacao_geral || 0}%
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Compatibilidade</p>
                  </div>
                </div>
                
                <div className="mt-4">
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold border ${getRecomendacaoStyle(candidatoSelecionado.recomendacao)}`}>
                    {getRecomendacaoLabel(candidatoSelecionado.recomendacao)}
                  </span>
                </div>

                {candidatoSelecionado.resumo_executivo && (
                  <div className="mt-4 p-4 bg-white/50 rounded-xl">
                    <p className="text-slate-700 italic">"{candidatoSelecionado.resumo_executivo}"</p>
                  </div>
                )}
              </div>

              {/* Pontuações Detalhadas */}
              {candidatoSelecionado.pontuacoes_detalhadas && (
                <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">📊 Pontuação Detalhada</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: "Técnico", value: candidatoSelecionado.pontuacoes_detalhadas.requisitos_tecnicos || 0, icon: "💻" },
                      { label: "Experiência", value: candidatoSelecionado.pontuacoes_detalhadas.experiencia || 0, icon: "💼" },
                      { label: "Formação", value: candidatoSelecionado.pontuacoes_detalhadas.formacao || 0, icon: "🎓" },
                      { label: "Soft Skills", value: candidatoSelecionado.pontuacoes_detalhadas.soft_skills || 0, icon: "🤝" },
                    ].map((item, idx) => (
                      <div key={idx} className="bg-slate-700/30 rounded-xl p-4 text-center">
                        <div className="text-2xl mb-2">{item.icon}</div>
                        <div className={`text-2xl font-bold bg-gradient-to-r ${getScoreColor(item.value)} bg-clip-text text-transparent`}>
                          {item.value}%
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
                <div className="flex border-b border-slate-700/50">
                  {[
                    { id: "resumo", label: "Resumo", icon: "📋" },
                    { id: "competencias", label: "Competências", icon: "🛠️" },
                    { id: "experiencia", label: "Experiência", icon: "💼" },
                    { id: "entrevista", label: "Entrevista", icon: "🎯" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 py-3 px-4 text-sm font-medium transition ${
                        activeTab === tab.id ? "bg-slate-700/50 text-white border-b-2 border-blue-500" : "text-slate-400 hover:text-white"
                      }`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-6">
                  {activeTab === "resumo" && (
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-white font-semibold mb-3">✓ Pontos Fortes</h4>
                        <div className="space-y-2">
                          {(candidatoSelecionado.pontos_fortes || []).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                              <span className="text-green-400">•</span>
                              <span className="text-slate-300 text-sm">{item}</span>
                            </div>
                          ))}
                          {(!candidatoSelecionado.pontos_fortes || candidatoSelecionado.pontos_fortes.length === 0) && (
                            <p className="text-slate-500 text-sm">Nenhum identificado</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-3">✗ Gaps</h4>
                        <div className="space-y-2">
                          {(candidatoSelecionado.gaps || []).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                              <span className="text-red-400">•</span>
                              <span className="text-slate-300 text-sm">{item}</span>
                            </div>
                          ))}
                          {(!candidatoSelecionado.gaps || candidatoSelecionado.gaps.length === 0) && (
                            <p className="text-slate-500 text-sm">Nenhum identificado</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "competencias" && (
                    <div className="space-y-3">
                      {(candidatoSelecionado.competencias_tecnicas || []).map((comp, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-4">
                          <div className="flex-1">
                            <p className="text-white font-medium">{comp.competencia}</p>
                            {comp.observacao && <p className="text-slate-400 text-sm mt-1">{comp.observacao}</p>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusStyle(comp.status)}`}>
                              {comp.status === "atende" ? "✓ Atende" : comp.status === "parcial" ? "◐ Parcial" : "✗ Não atende"}
                            </span>
                            <span className="px-2 py-1 bg-slate-600 text-slate-300 rounded text-xs">{comp.nivel}</span>
                          </div>
                        </div>
                      ))}
                      {(!candidatoSelecionado.competencias_tecnicas || candidatoSelecionado.competencias_tecnicas.length === 0) && (
                        <p className="text-slate-500 text-center py-4">Nenhuma competência analisada</p>
                      )}
                    </div>
                  )}

                  {activeTab === "experiencia" && (
                    <div className="space-y-4">
                      {candidatoSelecionado.experiencia_profissional && (
                        <>
                          <div className="flex items-center gap-6 bg-slate-700/30 rounded-lg p-4">
                            <div className="text-center">
                              <div className="text-3xl font-bold text-white">
                                {candidatoSelecionado.experiencia_profissional.anos_totais || 0}
                              </div>
                              <p className="text-slate-400 text-sm">Anos</p>
                            </div>
                            <div>
                              <p className="text-white">Experiência Total</p>
                              <span className={`inline-block mt-1 px-2 py-1 rounded text-xs ${
                                candidatoSelecionado.experiencia_profissional.relevancia === "alta" ? "bg-green-500/20 text-green-400" :
                                candidatoSelecionado.experiencia_profissional.relevancia === "media" ? "bg-yellow-500/20 text-yellow-400" :
                                "bg-red-500/20 text-red-400"
                              }`}>
                                Relevância {candidatoSelecionado.experiencia_profissional.relevancia}
                              </span>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-white font-semibold mb-3">🏆 Realizações</h4>
                            <div className="space-y-2">
                              {(candidatoSelecionado.experiencia_profissional.principais_realizacoes || []).map((item, idx) => (
                                <div key={idx} className="flex items-start gap-2 bg-slate-700/30 rounded-lg p-3">
                                  <span className="text-yellow-400">★</span>
                                  <span className="text-slate-300 text-sm">{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === "entrevista" && (
                    <div className="space-y-4">
                      <h4 className="text-white font-semibold">🎯 Perguntas Sugeridas</h4>
                      <div className="space-y-3">
                        {(candidatoSelecionado.perguntas_entrevista || []).map((pergunta, idx) => (
                          <div key={idx} className="flex items-start gap-3 bg-slate-700/30 rounded-lg p-4">
                            <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                              {idx + 1}
                            </span>
                            <p className="text-slate-300">{pergunta}</p>
                          </div>
                        ))}
                        {(!candidatoSelecionado.perguntas_entrevista || candidatoSelecionado.perguntas_entrevista.length === 0) && (
                          <p className="text-slate-500 text-center py-4">Nenhuma pergunta sugerida</p>
                        )}
                      </div>
                      {candidatoSelecionado.observacoes_adicionais && (
                        <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                          <h4 className="text-yellow-400 font-semibold mb-2">📝 Observações</h4>
                          <p className="text-slate-300 text-sm">{candidatoSelecionado.observacoes_adicionais}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Estado Inicial */}
          {!loading && modo === "lista" && !vagaSelecionada && (
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-12 text-center">
              <div className="text-6xl mb-4">💼</div>
              <h2 className="text-2xl font-bold text-white mb-2">Bem-vindo!</h2>
              <p className="text-slate-400 mb-6">Selecione uma vaga na sidebar ou crie uma nova para começar.</p>
              <button
                onClick={() => setModo("nova-vaga")}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700"
              >
                + Criar Nova Vaga
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadSection;
